import { useCallback, useMemo } from 'react';

import { useMessageInstanceEvent } from '@atlassian/post-office-context/message-lifecycle';

import type {
	AcknowledgedInput,
	ClosedInput,
	CtaActionedInput,
	DeclinedInput,
	PostOfficeUserInteractionHandlers,
	SnoozedInput,
	StepChangedInput,
} from '../types';
import { mapInteractionToClickedEvent, type SemanticInteraction } from './map-to-clicked-event';

export type { SemanticInteraction } from './map-to-clicked-event';

/**
 * Records what the user decided about a Post Office message.
 *
 * Each named handler corresponds to exactly one semantic interaction. Callers state the
 * decision and which control produced it; Post Office owns event construction, the
 * compatibility mapping, and all message, placement, trigger, journey, recommendation and
 * product context.
 *
 * Handlers are fire-and-forget and never navigate, close or dismiss the message — recording
 * an interaction and controlling product UI are separate concerns. Callers may write
 * `void onCtaActioned(...)` to make that explicit.
 *
 * Must be called inside the Post Office message and placement providers. When required
 * context is missing, the underlying client drops the event and reports it as an operational
 * event; Post Office-owned identifiers are never invented to make an analytic event sendable.
 *
 * @example
 * ```tsx
 * const { onCtaActioned, onClosed } = usePostOfficeUserInteractions();
 *
 * <Button
 *   onClick={() => {
 *     void onCtaActioned({ interactionIdentifier: 'start-trial', destinationUrl: trialUrl });
 *     navigate(trialUrl);
 *   }}
 * >
 *   Start trial
 * </Button>
 * ```
 */
export const usePostOfficeUserInteractions = (): PostOfficeUserInteractionHandlers => {
	const { messageInstanceClicked } = useMessageInstanceEvent();

	const record = useCallback(
		(interaction: SemanticInteraction): void => {
			// Dispatch is fire-and-forget: the caller must not wait for analytics before
			// continuing a UI action, and recording an interaction must never break the
			// message.
			messageInstanceClicked(mapInteractionToClickedEvent(interaction)).catch(() => {});
		},
		[messageInstanceClicked],
	);

	const onCtaActioned = useCallback(
		(input: CtaActionedInput) => record({ kind: 'ctaActioned', ...input }),
		[record],
	);

	const onAcknowledged = useCallback(
		(input: AcknowledgedInput) => record({ kind: 'acknowledged', ...input }),
		[record],
	);

	const onDeclined = useCallback(
		(input: DeclinedInput) => record({ kind: 'declined', ...input }),
		[record],
	);

	const onSnoozed = useCallback(
		(input: SnoozedInput) => record({ kind: 'snoozed', ...input }),
		[record],
	);

	const onClosed = useCallback(
		(input: ClosedInput) => record({ kind: 'closed', ...input }),
		[record],
	);

	const onStepChanged = useCallback(
		(input: StepChangedInput) => record({ kind: 'stepChanged', ...input }),
		[record],
	);

	return useMemo(
		() => ({
			onCtaActioned,
			onAcknowledged,
			onDeclined,
			onSnoozed,
			onClosed,
			onStepChanged,
		}),
		[onCtaActioned, onAcknowledged, onDeclined, onSnoozed, onClosed, onStepChanged],
	);
};
